export interface AnalyticsEvent {
  readonly name: string;
  readonly properties: Readonly<Record<string, string | number | boolean>>;
  readonly occurredAt: string;
  readonly sessionId: string;
}

export interface AnalyticsSnapshot {
  readonly sessionId: string;
  readonly startedAt: string;
  readonly eventCount: number;
  readonly recentEvents: readonly AnalyticsEvent[];
  readonly errors: readonly string[];
  readonly performance: {
    readonly frameSamples: number;
    readonly averageFrameMs: number;
    readonly worstFrameMs: number;
  };
}

const MAX_HISTORY = 250;
const MAX_ERRORS = 25;

function createSessionId(): string {
  return `defense-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export class AnalyticsManager {
  readonly history: AnalyticsEvent[] = [];
  private readonly errors: string[] = [];
  private readonly sessionId = createSessionId();
  private readonly startedAt = new Date().toISOString();
  private frameSamples = 0;
  private frameTotalMs = 0;
  private worstFrameMs = 0;

  track(name: string, properties: AnalyticsEvent["properties"] = {}): void {
    const event: AnalyticsEvent = {
      name,
      properties,
      occurredAt: new Date().toISOString(),
      sessionId: this.sessionId,
    };
    this.history.push(event);
    if (this.history.length > MAX_HISTORY) this.history.shift();
    if (import.meta.env.DEV) console.debug("[Defense analytics]", name, properties);
  }

  recordError(error: unknown, context = "runtime"): void {
    const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
    this.errors.push(`${context}: ${message}`);
    if (this.errors.length > MAX_ERRORS) this.errors.shift();
    this.track("client_error", { context, message: message.slice(0, 240) });
  }

  sampleFrame(frameMs: number): void {
    if (!Number.isFinite(frameMs) || frameMs <= 0) return;
    this.frameSamples += 1;
    this.frameTotalMs += frameMs;
    this.worstFrameMs = Math.max(this.worstFrameMs, frameMs);
    if (frameMs >= 80) this.track("performance_warning", { frameMs: Math.round(frameMs) });
  }

  snapshot(): AnalyticsSnapshot {
    return {
      sessionId: this.sessionId,
      startedAt: this.startedAt,
      eventCount: this.history.length,
      recentEvents: this.history.slice(-50),
      errors: [...this.errors],
      performance: {
        frameSamples: this.frameSamples,
        averageFrameMs: this.frameSamples > 0 ? this.frameTotalMs / this.frameSamples : 0,
        worstFrameMs: this.worstFrameMs,
      },
    };
  }
}

export const defenseAnalytics = new AnalyticsManager();

export function installGlobalObservability(): void {
  window.addEventListener("error", (event) => {
    defenseAnalytics.recordError(event.error ?? event.message, "window.error");
  });
  window.addEventListener("unhandledrejection", (event) => {
    defenseAnalytics.recordError(event.reason, "unhandledrejection");
  });
  document.addEventListener("visibilitychange", () => {
    defenseAnalytics.track("visibility_changed", { hidden: document.hidden });
  });
}
